/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../../routing/state-utils';
import {MarketplaceProductCopyModalComponent} from './marketplace-product-copy-modal.component';
import {MarketplaceProductApiService, MarketplaceProductModel} from './marketplace-product-api.service';
import {MarketplaceProductEditPageComponent} from './marketplace-product-edit-page.component';
import {MarketplaceProductRoutePageComponent} from './marketplace-product-route-page.component';

export function marketplaceProductStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(MarketplaceProductApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => {
    const perms = permissions.getAllNetworkPermissions();
    return !perms.AX_PUBLISHER_ADMIN && !perms.MANAGE_SYSTEM;
  };
  const rh = adnRouterHelper.createRouterHelper(
    'marketplace-products.marketplace-product', MarketplaceProductApiService, 'app.adm', 'app/admin'
  );
  const copyConfig = {
    modalComponent: MarketplaceProductCopyModalComponent,
    stateUrl: 'app.adm.marketplace-products.marketplace-product',
    lcase: 'marketplace product',
    ucase: 'Marketplace Product'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {readOnly},
    views: {
      'content@app': {component: MarketplaceProductRoutePageComponent}
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      marketplaceProductPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: MarketplaceProductModel = {
            id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []
          };
          return new SignalAdminResourcePageState(
            model, true, readOnly(), api, 'Marketplace Product', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Marketplace Product', 'name', copyConfig
        ));
      },
      model: (marketplaceProductPage: SignalAdminResourcePageState<MarketplaceProductModel>) => marketplaceProductPage.model()
    },
    views: {
      'content@app': {component: MarketplaceProductEditPageComponent, bindings: {state: 'marketplaceProductPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'marketplaceProductPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'marketplaceProductPage'}}
    }
  }));
  return states.build();
}
